import { StandardTextFieldProps, Typography } from '@material-ui/core';
import { AutocompleteChangeReason } from '@material-ui/lab';
import { Inline, Stack } from '@superdispatch/ui';
import { FormikValues, useField, useFormikContext } from 'formik';
import { useMemo, useState } from 'react';
import { FIFTH_OF_A_SECOND } from 'shared/constants/NumberConstants';
import { useDebouncedValue } from 'shared/helpers/ReactHelpers';
import { ContactDTO } from '../../contacts/data/ContactDTO';
import { useContactsList } from '../../contacts/data/ContactsAPI';
import { OrdersDTO } from '../data/LoadDTO';
import { EmailAutocomplete } from '@superdispatch/ui-lab';

interface LoadFieldCommonProps {
  TextFieldProps?: StandardTextFieldProps;
}

interface EmailFieldMultipleProps extends Omit<
  LoadFieldCommonProps,
  'options' | 'onChange' | 'getOptionLabel'
> {
  name: string;
  onChange?: (
    value: OrdersDTO | undefined,
    reason: AutocompleteChangeReason,
  ) => void;
}

export function LoadEmailSelectionField({
  name,
  onChange,
  TextFieldProps,
  ...props
}: EmailFieldMultipleProps) {
  const { setValues } = useFormikContext<FormikValues>();
  const [field, { touched, error }] = useField<ContactDTO[]>({ name });
  const [isEmailEnabled] = useField<boolean>({ name: 'email' });

  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search, FIFTH_OF_A_SECOND);

  const errorMessage = touched && error;

  const { data, isFetching } = useContactsList(
    { q: query, page_size: 50 },
    { enabled: !!query, staleTime: Infinity, gcTime: Infinity },
    true,
  );

  const emailOptions = useMemo(
    () => (data ? data.pages.flatMap((item) => item.data) : null),
    [data],
  );

  const options = useMemo(
    () =>
      emailOptions && emailOptions.length > 0
        ? emailOptions.map((option) => option.email)
        : [search],
    [emailOptions, search],
  );

  const value = useMemo(
    () => field.value.map((item) => item.email),
    [field.value],
  );

  const contactsEmailMap = useMemo<Record<string, ContactDTO>>(
    () =>
      emailOptions?.reduce(
        (acc, item) => ({ ...acc, [item.email]: item }),
        {},
      ) || {},
    [emailOptions],
  );

  return (
    <EmailAutocomplete
      {...props}
      options={options}
      aria-label="contact-list"
      value={value}
      loading={isFetching}
      inputValue={search}
      disabled={!isEmailEnabled.value}
      onInputChange={(_event, changedInputValue) => {
        setSearch(changedInputValue);
      }}
      filterOptions={(filterOptions) => {
        return filterOptions.filter((option) => option !== '');
      }}
      TextFieldProps={{
        disabled: !isEmailEnabled.value,
        error: !!errorMessage,
        helperText: errorMessage,
        multiline: true,
        minRows: 2,
        variant: 'outlined',
        fullWidth: true,
        placeholder:
          'Enter individual emails on each line or separate them with comma (,)',
      }}
      onChange={(selectedValue = []) => {
        void setValues((prev) => ({
          ...prev,
          emails: selectedValue.map(
            (email) => contactsEmailMap[email] || { email, name: '', guid: '' },
          ),
        }));
      }}
      renderOption={(option) => (
        <Stack>
          <Inline space="xxsmall" verticalAlign="center">
            <Typography variant="h6">
              {contactsEmailMap[option]?.name}
            </Typography>
            <Typography variant="body2">{option}</Typography>
          </Inline>
        </Stack>
      )}
    />
  );
}
